import { useEffect } from "react"
import type { QueryClient } from "@tanstack/react-query"
import { get } from "radash"

import {
  v2CustomerGetCurrentUserStatus,
  type v2CustomerFindingDriverStatus,
} from "@be-miniapp/api"
import { BEAutoRideStatus, type TripState } from "@be-miniapp/types"

import { activeSessionsQueryKey } from "../queries/use-active-sessions"
import type { HydrateInTripSessionParams } from "../stores/ride-data"
import {
  buildHydrateParamsFromEngagement,
  buildHydrateParamsFromFindingDriver,
  deriveTripStateFromEngagementStatus,
} from "./ride-intrip-hydration"

/** Minimal slice of active-sessions list item used by preload resolution. */
export interface InTripPreloadSessionRow {
  trip_id: number
  is_schedule?: boolean
  is_finding_driver?: boolean
}

export interface InTripPreloadActiveSessionsShape {
  sessions?: InTripPreloadSessionRow[]
}

export interface InTripPreloadRegionRow {
  vehicle_type?: number
}

export interface InTripPreloadNavigateOptions {
  replace?: boolean
  animate?: boolean
  state?: Record<string, unknown>
}

export type InTripPreloadNavigate = (
  to: string,
  options?: InTripPreloadNavigateOptions
) => void

/**
 * Resolves `session_id` query param → hydrate ride store → navigate to in-trip or fallbacks.
 * Shared by AppView and Zalo; apps supply `navigate`, query client, and store actions.
 */
export function useInTripPreloadSession(options: {
  sessionId: string | null
  activeSessions: InTripPreloadActiveSessionsShape | undefined | null
  navigate: InTripPreloadNavigate
  queryClient: QueryClient
  regions: InTripPreloadRegionRow[] | undefined
  setSessionId: (tripId: number) => void
  hydrateInTripSession: (params: HydrateInTripSessionParams) => void
  changeActivityTab: (tab: string) => void
}): void {
  const {
    sessionId,
    activeSessions,
    navigate,
    queryClient,
    regions,
    setSessionId,
    hydrateInTripSession,
    changeActivityTab,
  } = options

  useEffect(() => {
    if (!activeSessions) return

    const session = activeSessions.sessions?.find(
      (s) => s.trip_id === Number(sessionId)
    )

    if (!session) {
      setTimeout(() => {
        changeActivityTab("transport")
        navigate("/ride/home/history", {
          replace: true,
          animate: false,
        })
      }, 1000)
      return
    }

    setSessionId(session.trip_id)

    if (session.is_schedule) {
      navigate("/non-supported", {
        state: {
          isSession: true,
        },
      })
      return
    }

    void (async () => {
      const sessionData = await v2CustomerGetCurrentUserStatus({
        session_id: session.trip_id,
      })

      if (sessionData.flag === 131) {
        queryClient.invalidateQueries({
          queryKey: activeSessionsQueryKey.all,
        })
      }

      if (sessionData.flag === 132) {
        const engagement = sessionData?.last_engagement_info?.[0]

        if (!engagement) return

        if (
          engagement.status === BEAutoRideStatus.ended ||
          engagement.status === BEAutoRideStatus.rideCanceledByCustomer
        ) {
          navigate(`/activities/transport/detail/${engagement.engagement_id}`, {
            state: {
              forceBackToHome: true,
            },
          })

          return
        }

        if (engagement.is_scheduled_booking) {
          navigate("/non-supported", {
            state: {
              isSession: true,
            },
          })
          return
        }
        if ((engagement.stop_points?.length ?? 0) > 0) {
          navigate("/non-supported", {
            state: {
              isSession: true,
            },
          })
          return
        }

        if (get(engagement, "befar.going_back_pickup_address")) {
          navigate("/non-supported", {
            state: {
              isSession: true,
            },
          })
          return
        }

        if (get(engagement, "rider.name")) {
          navigate("/non-supported", {
            state: {
              isSession: true,
            },
          })
          return
        }

        const nextState: TripState = deriveTripStateFromEngagementStatus({
          status: engagement.status,
          driverInPerfectRide: engagement.driver_in_perfect_ride,
        })

        hydrateInTripSession(
          buildHydrateParamsFromEngagement({
            sessionId: session.trip_id,
            engagement,
            nextState,
          })
        )

        navigate("/ride/in-trip", {
          state: {
            sessionId: session.trip_id,
            region: regions?.find(
              (region) => region.vehicle_type === engagement.vehicle_type
            ),
          },
        })
      }

      if (sessionData.flag === 105 && session.is_finding_driver) {
        const findingDriverData =
          sessionData as unknown as v2CustomerFindingDriverStatus

        hydrateInTripSession(
          buildHydrateParamsFromFindingDriver({
            sessionId: session.trip_id,
            findingDriverData,
            preferredPaymentMethod: findingDriverData.preferred_payment_mode,
          })
        )

        navigate("/ride/in-trip", {
          state: {
            sessionId: session.trip_id,
            region: regions?.find(
              (region) => region.vehicle_type === findingDriverData.vehicle_type
            ),
          },
        })
      }
    })()
  }, [
    activeSessions,
    changeActivityTab,
    hydrateInTripSession,
    navigate,
    queryClient,
    regions,
    sessionId,
    setSessionId,
  ])
}
